/**
 * What Walk it through holds whichever layout draws it: the step the address names, which steps were looked at, the
 * room's name, the page's studio and the last change an Undo takes back. The computer's screen and the phone's and
 * iPad's both read it, so a step looked at on one is looked at on the other.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import type { LivePageTime } from "../../../shared/live-page";
import type { BoardState, PageMessage } from "../../../shared/types";
import { useToast } from "../../components/ui/Toast";
import type { BoardHost } from "../host";
import { usePageStudio } from "./pageStudio";
import { offerOf } from "./roomParts";

export const WALK_STEPS = ["page", "room", "followups"] as const;
export type WalkStep = (typeof WALK_STEPS)[number];
export const STEP_NAMES: Record<WalkStep, string> = { page: "The page", room: "In the room", followups: "Follow-ups" };

/** The step the address names, else the one the host was last on, else the page. The old offer step is the room's. */
function stepOf(v: string | null, saved: string | undefined): WalkStep {
  const known = (s: string | null | undefined): WalkStep | null => (s === "offer" ? "room" : (WALK_STEPS as readonly string[]).includes(s ?? "") ? (s as WalkStep) : null);
  return known(v) ?? known(saved) ?? "page";
}

/** The page's address without its own part: "maya-torres.flashroom.ai". */
function addressBase(registerUrl: string, slug: string): string {
  const bare = registerUrl.replace(/^https?:\/\//, "").replace(/\/+$/, "");
  return slug && bare.endsWith(`/${slug}`) ? bare.slice(0, -(slug.length + 1)) : bare.replace(/\/[^/]*$/, "") || bare;
}

/** The changes in these threads still standing: a reply that changed something, not taken back. */
function standing(threads: Record<string, PageMessage[]> | undefined): number {
  return Object.values(threads ?? {}).reduce((n, t) => n + t.filter((m) => m.who === "ai" && m.before !== undefined && !m.taken).length, 0);
}

/** What starting over loses, counted honestly: the changes still standing on the page. */
export function lostChanges(pageThreads: Record<string, PageMessage[]>): Array<[number, string]> {
  const page = standing(pageThreads);
  return [[page, page === 1 ? "change to the page" : "changes to the page"]];
}

/** The last change the bar's Undo takes back: on the page, or words typed in place (and a pin dragged). */
type Last = { kind: "page"; section: string; id: string } | { kind: "typed"; undo: (now: BoardState) => void };

export function useWalk(state: BoardState, host: BoardHost) {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const step = stepOf(params.get("step"), state.walk?.step);
  const [last, setLast] = useState<Last | null>(null);
  const stateNow = useRef(state);
  stateNow.current = state;
  const server = host.server;
  const live = server.scheduleMode === "live";
  // A live's time is filled in exactly as the served page fills it.
  const session = server.live;
  const liveTime: LivePageTime | null = useMemo(
    () => (session ? { startsAt: session.startsAt, timezone: session.timezone, state: session.state } : null),
    [session?.startsAt, session?.timezone, session?.state],
  );
  const chaptersCard = state.page.find((c) => c.kind === "chapters");
  const chapterKey = chaptersCard && chaptersCard.kind === "chapters" ? JSON.stringify(chaptersCard.rows.map((r) => [r.t, r.title])) : "";
  const studio = usePageStudio(host.webinarId, liveTime, chapterKey);
  const looked = new Set(state.walk?.looked ?? []);
  const published = server.status === "published";
  const offer = offerOf(state);
  const at = WALK_STEPS.indexOf(step);
  const base = addressBase(host.liveUrl || "", state.slug);
  const minutes = host.duration ? Math.max(1, Math.round(host.duration / 60)) : 0;
  const titleCard = state.page.find((c) => c.kind === "title");
  /** The room's name as the board holds it: its title card, else the working title, else the server's. */
  const roomName = (titleCard && titleCard.kind === "title" ? titleCard.v : "") || state.workingTitle || server.title;
  const address = host.liveUrl || `${base}/${state.slug}`;

  useEffect(() => {
    if (studio.lastChange) setLast({ kind: "page", ...studio.lastChange });
  }, [studio.lastChange]);

  const undoLast = () => {
    if (!last) return;
    setLast(null);
    if (last.kind === "page") void studio.back(last.section, last.id);
    else {
      last.undo(state);
      toast({ text: "Back to how it was.", icon: "undo" });
    }
  };
  /** Words typed in place (or a pin moved): the bar's Undo takes it back; so does the way back this returns. */
  const remember = useCallback(
    (undo: (now: BoardState) => void) => {
      const entry = { kind: "typed" as const, undo };
      setLast(entry);
      return () => {
        setLast((l) => (l === entry ? null : l));
        undo(stateNow.current);
        toast({ text: "Back to how it was.", icon: "undo" });
      };
    },
    [toast],
  );

  // The step the host is on rides on the board: Home says where the draft stands, and Continue opens it here.
  useEffect(() => {
    if (state.walk?.step !== step) host.dispatch({ type: "walkStep", step });
    // `host` changes every render; the step and the board's own record are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, state.walk?.step]);

  const go = (k: WalkStep) => setParams({ step: k });
  /** "Looks right": this step is looked at, and the next one opens; the last one says what to do at the end. */
  const next = (atEnd: () => void) => {
    host.dispatch({ type: "looked", step, on: true });
    if (at === WALK_STEPS.length - 1) {
      if (published) toast("It looks right.");
      else atEnd();
      return;
    }
    go(WALK_STEPS[at + 1]);
  };
  /** The name goes where it is kept: the title card when the board has one, else the working title. */
  const rename = (v: string) => {
    if (titleCard && titleCard.kind === "title") host.dispatch({ type: "edit", id: titleCard.id, patch: { v } as never });
    else host.dispatch({ type: "workingTitle", title: v });
  };
  /** What a step says under its name in the list of steps. */
  const info: Record<WalkStep, string> = {
    page: "",
    room: "",
    followups: `${state.mail.length} ${state.mail.length === 1 ? "automation" : "automations"}`,
  };

  return {
    navigate, params, setParams, step, at, go, next, looked, published, live, server, studio, offer, base, minutes,
    titleCard, chaptersCard, roomName, rename, address, info, last, undoLast, remember,
  };
}
